import React, { useCallback, useEffect, useMemo } from 'react';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { asyncAlertsContentViewQuery } from './alerts-content/asyncAlertsContentViewQuery.entrypoint.tsx';
import { OperationsExpandableMenuItem } from './operations-expandable-menu-item/index.tsx';
import { useAlertsPopoutStore } from './operations-expandable-menu-item/action-buttons/useAlertsPopoutStore.tsx';

export type OperationsMenuDefaultProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
};

export const OperationsMenuDefault: React.FC<OperationsMenuDefaultProps> = componentWithFG(
	'navx-global-side-nav',
	OperationsMenuDefaultGSN,
	OperationsMenuDefaultNav4,
);

function OperationsMenuDefaultGSN(props: OperationsMenuDefaultProps): React.JSX.Element {
	const { isExpanded, ...dndProps } = props;
	const cloudId = useCloudId();

	const entryPointParams = useMemo(() => ({ cloudId }), [cloudId]);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncAlertsContentViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);

	const {
		state: { lastVisitedSearch },
		setContent,
	} = useAlertsPopoutStore();

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, isExpanded]);

	useEffect(() => {
		if (lastVisitedSearch) {
			setContent(
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates.
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="operations-menu-default"
					packageName="jiraNavigationAppsSidebarNav4Operations"
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName="platform-united"
					// Fallback for `Suspense`.
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component.
					runtimeProps={{ ...entryPointParams, location: 'popOutPanel' }}
				/>,
			);
		}
	}, [entryPointParams, entryPointReferenceSubject, lastVisitedSearch, setContent]);

	const handleAnyToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	return (
		<OperationsExpandableMenuItem
			AlertsMenuItemContent={
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates.
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="operations-menu-default"
					packageName="jiraNavigationAppsSidebarNav4Operations"
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName="platform-united"
					// Fallback for `Suspense`.
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component.
					runtimeProps={{ ...entryPointParams, location: 'sidenav' }}
				/>
			}
			isExpanded={isExpanded}
			onExpansionToggle={handleAnyToggle}
			onSystemToggle={handleAnyToggle}
			triggerRef={triggerRef}
			{...dndProps}
		/>
	);
}

function OperationsMenuDefaultNav4(props: OperationsMenuDefaultProps): React.JSX.Element {
	const { isExpanded } = props;
	const cloudId = useCloudId();

	const entryPointParams = useMemo(() => ({ cloudId }), [cloudId]);
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncAlertsContentViewQuery,
		entryPointParams,
	);
	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);

	const {
		state: { lastVisitedSearch },
		setContent,
	} = useAlertsPopoutStore();

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, isExpanded]);

	useEffect(() => {
		if (lastVisitedSearch) {
			setContent(
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates.
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="operations-menu-default"
					packageName="jiraNavigationAppsSidebarNav4Operations"
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName="platform-united"
					// Fallback for `Suspense`.
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component.
					runtimeProps={{ ...entryPointParams, location: 'popOutPanel' }}
				/>,
			);
		}
	}, [entryPointParams, entryPointReferenceSubject, lastVisitedSearch, setContent]);

	return (
		<OperationsExpandableMenuItem
			isExpanded={props.isExpanded}
			onSystemToggle={(hasExpanded: boolean) => {
				if (hasExpanded) {
					entryPointActions.load();
				}
			}}
			onExpansionToggle={(hasExpanded: boolean) => {
				if (hasExpanded) {
					entryPointActions.load();
				}
			}}
			triggerRef={triggerRef}
			AlertsMenuItemContent={
				<JiraEntryPointContainer
					// Data stream, will subscribe to load updates.
					entryPointReferenceSubject={entryPointReferenceSubject}
					// Required props for error JSErrorBoundary.
					id="operations-menu-default"
					packageName="jiraNavigationAppsSidebarNav4Operations"
					// Optional props for error JSErrorBoundary.
					errorFallback="flag"
					teamName="platform-united"
					// Fallback for `Suspense`.
					fallback={<SkeletonNavMenuItems />}
					// Props for the root component.
					runtimeProps={{ ...entryPointParams, location: 'sidenav' }}
				/>
			}
		/>
	);
}
